import { Bell, Menu } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { IconButton } from '@/components/ui/IconButton'
import { LanguageSelect } from '@/components/ui/LanguageSelect'
import { GlobalSearch } from '@/features/search/components/GlobalSearch'
import type { CurrentUser } from '@/types/domain'

export interface TopbarProps {
  user: CurrentUser
  unreadCount?: number
  onOpenMenu: () => void
  onOpenNotifications?: () => void
  onLanguageChange?: (language: 'en' | 'ru') => void
  /** Passed straight to the search box, which hands back a focus action for
   *  the `/` shortcut. The shortcut lives in the shell because it must work
   *  from anywhere on the page, not only while the topbar has focus. */
  onRegisterSearchFocus?: (focus: (() => void) | null) => void
}

/** Persistent chrome: search, notification bell, language.
 *
 *  The profile block lives in the sidebar footer, not here — it is the only
 *  route to /profile, so SidebarNav carries that link. */
export function Topbar({
  user,
  unreadCount = 0,
  onOpenMenu,
  onOpenNotifications,
  onLanguageChange,
  onRegisterSearchFocus,
}: TopbarProps) {
  const { t } = useTranslation()

  return (
    <header className="bg-surface border-border-subtle flex h-[var(--spacing-topbar)] shrink-0 items-center gap-3.5 border-b px-4 sm:px-6">
      <IconButton
        icon={Menu}
        label={t('common.menu.open')}
        onClick={onOpenMenu}
        className="lg:hidden"
      />

      {/* No width here: the box sizes ITSELF, growing while it is in use, so a
          fixed width on the wrapper would clamp the animation it exists for. */}
      <GlobalSearch
        onRegisterFocus={onRegisterSearchFocus}
        className="hidden min-w-0 sm:block"
      />

      <div className="flex-1" />

      {/* Both languages are first-class: Russian is not a translation layer
          over an English product (FR-58). Hidden
          on the narrowest widths only because the same switch is on Profile —
          the preference is never unreachable, just not in cramped chrome. */}
      <LanguageSelect
        value={user.language}
        onChange={(language) => onLanguageChange?.(language)}
        className="hidden w-auto sm:block"
      />

      <div className="relative">
        <IconButton
          icon={Bell}
          label={
            unreadCount > 0
              ? t('common.notifications.labelWithCount', { count: unreadCount })
              : t('common.notifications.label')
          }
          onClick={onOpenNotifications}
        />
        {unreadCount > 0 ? (
          <span
            aria-hidden
            className="bg-brand ring-surface absolute top-1 right-1 size-[7px] rounded-full ring-2"
          />
        ) : null}
      </div>

    </header>
  )
}
